import { MARKETING_THEME } from "@/lib/theme";
import { useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { homePathFor } from "@/lib/roles";
import { useSiteContent } from "@/hooks/useSiteContent";
import { SmsTermsNotice } from "@/components/marketing/SmsConsent";
import { SmsTermsBlock, SMS_MARKETING_CONSENT_TEXT } from "@/components/marketing/SmsTerms";
import { Input } from "@/components/ui/input";
import { AuthCard } from "@/components/auth/AuthCard";
import { Navbar } from "@/components/marketing/Navbar";
import { Footer } from "@/components/marketing/Footer";
import { CATEGORIES, CONSENT_TEXT, ConsentRow, Tick, TwoFactorStep as TextCodeConsentStep } from "@/pages/CommsOptIn";
import { TwoFactorStep } from "@/components/auth/TwoFactorStep";

const startGoogle = () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/signin";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const GoogleIcon = () => (
  <svg viewBox="0 0 48 48" className="h-4 w-4" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
  </svg>
);

const lbl = "block font-mono-label text-[10px] font-semibold text-oath-secondary";

const Preferences = ({ p, setP, support }) => {
  const set = (k) => (e) => setP({ ...p, [k]: e.target.value });
  const toggleCat = (id) => setP({ ...p, cats: { ...p.cats, [id]: !p.cats[id] } });
  return (
    <section className="mt-8 border-t border-gray-100 pt-6" data-testid="signin-preferences">
      <div className="font-mono-label text-[10px] font-semibold text-oath-accent">COMMUNICATION PREFERENCES · OPTIONAL</div>
      <p className="mt-1 text-sm text-oath-secondary">Subscribe to emails and texts. You don't need an account to subscribe, and signing in doesn't require it.</p>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <div><label className={lbl}>FULL NAME</label><Input value={p.full_name} onChange={set("full_name")} className="mt-2" placeholder="Jordan Avery" data-testid="optin-name" /></div>
        <div><label className={lbl}>MOBILE PHONE</label><Input value={p.phone} onChange={set("phone")} className="mt-2" placeholder="+1 555 000 0000" data-testid="optin-phone" /></div>
      </div>
      <label className={`${lbl} mt-5`}>I WOULD LIKE TO RECEIVE</label>
      <div className="mt-2 grid gap-2">
        {CATEGORIES.map((c) => (
          <div key={c.id} role="button" tabIndex={0} onClick={() => toggleCat(c.id)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleCat(c.id); } }}
            data-testid={`optin-cat-${c.id}`} data-state={p.cats[c.id] ? "checked" : "unchecked"}
            className={`flex cursor-pointer items-start gap-3 rounded-2xl border p-3 transition-colors ${p.cats[c.id] ? "border-oath-accent bg-oath-accent-light" : "border-gray-200 bg-white hover:border-gray-300"}`}>
            <Tick checked={p.cats[c.id]} />
            <span><span className="block text-sm font-semibold text-oath-ink">{c.title}</span><span className="block text-xs text-oath-secondary">{c.desc}</span></span>
          </div>
        ))}
      </div>
      <div className="mt-3"><ConsentRow checked={p.consent} onToggle={() => setP({ ...p, consent: !p.consent })} text={CONSENT_TEXT} testid="optin-consent" /></div>
      <label className={`${lbl} mt-5`}>PROMOTIONAL &amp; MARKETING TEXT MESSAGES</label>
      <div className="mt-2"><ConsentRow checked={p.sms} onToggle={() => setP({ ...p, sms: !p.sms })} text={SMS_MARKETING_CONSENT_TEXT} testid="optin-sms-consent" /></div>
      <div className="mt-3"><SmsTermsBlock support={support} /></div>
    </section>
  );
};

const EMPTY_PREFS = { full_name: "", phone: "", cats: { promotional: false, marketing: false, account_updates: false }, consent: false, sms: false };

const prefsError = (p, email) => {
  const selected = Object.keys(p.cats).filter((k) => p.cats[k]);
  if (!selected.length && !p.sms) return null;
  if (!selected.length) return "Choose at least one message type to subscribe.";
  if (!p.consent) return "Tick the consent box to subscribe.";
  if (!email) return "Enter your email address to subscribe.";
  if (p.full_name.trim().length < 2 || p.phone.trim().length < 5) return "Add your full name and mobile phone to subscribe.";
  return "";
};

const UnifiedForm = ({ onSignedIn, onSubscribed, error }) => {
  const { contact } = useSiteContent();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [p, setP] = useState(EMPTY_PREFS);
  const [loading, setLoading] = useState("");
  const [err, setErr] = useState(error || "");

  const savePrefs = async () => {
    const { data } = await api.post("/opt-in", {
      full_name: p.full_name, email, phone: p.phone, categories: Object.keys(p.cats).filter((k) => p.cats[k]),
      consent: true, consent_text: CONSENT_TEXT, sms_marketing_consent: p.sms,
      sms_consent_text: p.sms ? SMS_MARKETING_CONSENT_TEXT : "", website: "",
    });
    return data;
  };

  const run = async (mode) => {
    setErr("");
    const pe = prefsError(p, email);
    if (pe && mode === "subscribe") return setErr(pe);
    if (mode === "subscribe" && pe === null) return setErr("Choose at least one message type to subscribe.");
    if (mode === "signin" && (!email || !password)) return setErr("Enter your email and password to sign in.");
    setLoading(mode);
    try {
      if (mode === "subscribe") {
        onSubscribed(await savePrefs());
        return;
      }
      const { data: challenge } = await api.post("/auth/login", { email, password });
      let optIn = null;
      if (pe) {
        toast.warning(`Signed in, but preferences weren't saved: ${pe}`);
      }
      if (pe === "") {
        try {
          optIn = await savePrefs();
        } catch (e2) {
          toast.error(`Signed in, but preferences weren't saved: ${formatApiError(e2)}`);
        }
      }
      onSignedIn(challenge, optIn);
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setLoading("");
    }
  };

  return (
    <form onSubmit={(e) => { e.preventDefault(); run("signin"); }} data-testid="signin-form">
      <span className="grid h-11 w-11 place-items-center rounded-xl bg-oath-ink text-white"><Lock className="h-5 w-5" strokeWidth={1.75} /></span>
      <h1 className="mt-6 font-display text-3xl font-bold tracking-tight text-oath-ink">Sign in or subscribe</h1>
      <p className="mt-2 text-sm text-oath-secondary">Staff and approved clients sign in with email and password. Anyone can subscribe to updates below.</p>
      {err && <div className="mt-5 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="signin-error">{err}</div>}
      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <div><label className={lbl}>EMAIL</label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-2" autoComplete="username" data-testid="signin-email-input" /></div>
        <div><label className={lbl}>PASSWORD (ONLY TO SIGN IN)</label><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-2" autoComplete="current-password" data-testid="signin-password-input" /></div>
      </div>
      <Preferences p={p} setP={setP} support={contact.support_email} />
      <div className="mt-6 grid gap-3 sm:grid-cols-2">
        <button type="submit" disabled={!!loading} data-testid="signin-submit-button"
          className="inline-flex items-center justify-center gap-2 rounded-full bg-oath-ink px-6 py-3.5 font-mono-label text-[12px] font-semibold text-white transition-colors hover:bg-black disabled:opacity-60">
          {loading === "signin" ? <Loader2 className="h-4 w-4 animate-spin" /> : "SIGN IN"}
        </button>
        <button type="button" disabled={!!loading} onClick={() => run("subscribe")} data-testid="optin-submit"
          className="inline-flex items-center justify-center gap-2 rounded-full border border-oath-ink px-6 py-3.5 font-mono-label text-[12px] font-semibold text-oath-ink transition-colors hover:bg-gray-50 disabled:opacity-60">
          {loading === "subscribe" ? <Loader2 className="h-4 w-4 animate-spin" /> : "SUBSCRIBE ONLY"}
        </button>
      </div>
      <div className="my-5 flex items-center gap-3 font-mono-label text-[10px] text-oath-muted">
        <span className="h-px flex-1 bg-gray-200" />OR<span className="h-px flex-1 bg-gray-200" />
      </div>
      <button type="button" onClick={startGoogle} data-testid="signin-google-button"
        className="inline-flex w-full items-center justify-center gap-3 rounded-full border border-gray-300 bg-white px-6 py-3 text-sm font-medium text-oath-ink transition-colors hover:bg-gray-50">
        <GoogleIcon /> Continue with Google
      </button>
      <p className="mt-6 text-xs leading-relaxed text-oath-muted">
        Forgot your password? Ask your administrator for a reset link, or email {contact.support_email}.
        No account yet? <Link to="/signup" className="text-oath-accent underline" data-testid="signin-signup-business-link">Create a business account</Link> or a <Link to="/signup/personal" className="text-oath-accent underline" data-testid="signin-signup-personal-link">free personal account</Link>.
      </p>
      <SmsTermsNotice />
    </form>
  );
};

const Subscribed = ({ optIn, twofa, support }) => (
  <div className="text-center" data-testid="optin-success">
    <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-600" />
    <h1 className="mt-4 font-display text-2xl font-bold text-oath-ink">You're subscribed.</h1>
    <p className="mt-3 text-sm text-oath-secondary">Change your choices anytime by replying STOP to texts, using the unsubscribe link in any email, or contacting {support}.</p>
    <ul className="mx-auto mt-4 grid max-w-xs gap-1 text-left text-sm text-oath-secondary" data-testid="optin-summary">
      <li>Marketing &amp; promotional texts: <b>{optIn.sms_marketing_consent ? "Yes" : "No"}</b></li>
      <li>Two-factor codes by text: <b>{twofa ? "Yes" : "No"}</b></li>
    </ul>
    <Link to="/" className="mt-6 inline-block text-sm text-oath-accent underline" data-testid="optin-home-link">Back to home</Link>
  </div>
);

export default function SignIn() {
  const { user, setUser } = useAuth();
  const { contact } = useSiteContent();
  const location = useLocation();
  const navigate = useNavigate();
  const [challenge, setChallenge] = useState(location.state?.challenge || null);
  const [optIn, setOptIn] = useState(null);
  const [stage, setStage] = useState(location.state?.challenge ? "totp" : "form");
  const [twofa, setTwofa] = useState(null);

  if (user) return <Navigate to={location.state?.from || homePathFor(user)} replace />;

  const done = (u) => {
    setUser(u);
    navigate(homePathFor(u), { replace: true });
  };

  if (stage === "totp") {
    return <AuthCard testid="signin-panel"><TwoFactorStep challenge={challenge} onDone={done} /></AuthCard>;
  }

  return (
    <div className={`min-h-screen bg-oath-bg ${MARKETING_THEME}`}>
      <Navbar />
      <main className="mx-auto max-w-2xl px-5 pb-24 pt-32">
        {stage === "text-2fa" ? (
          <TextCodeConsentStep optIn={optIn} support={contact.support_email} embedded stepLabel={challenge ? "STEP 2 OF 3" : "STEP 2 OF 2"}
            onDone={(v) => { setTwofa(v); setStage(challenge ? "totp" : "done"); window.scrollTo({ top: 0 }); }} />
        ) : (
          <div className="animate-fade-up rounded-3xl border border-gray-200 bg-white p-7 shadow-sm sm:p-8" data-testid="signin-panel">
            {stage === "done" ? <Subscribed optIn={optIn} twofa={twofa} support={contact.support_email} /> : (
              <UnifiedForm
                error={location.state?.error}
                onSignedIn={(c, o) => { setChallenge(c); setOptIn(o); setStage(o ? "text-2fa" : "totp"); window.scrollTo({ top: 0 }); }}
                onSubscribed={(o) => { setOptIn(o); setStage("text-2fa"); window.scrollTo({ top: 0 }); }}
              />
            )}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
